import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LogOut, Menu, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { NotificationsBell } from "./NotificationsBell";

const NavItems = ({ items, onClick }) => (
  <nav className="flex flex-col gap-0.5">
    {items.map((it) => (
      <NavLink key={it.to} to={it.to} end={it.end} onClick={onClick} data-testid={`app-nav-${it.id}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-white text-[#0F2840]" : "text-white/75 hover:bg-white/10 hover:text-white"}`}>
        <it.icon className="h-4 w-4" />{it.label}
      </NavLink>
    ))}
  </nav>
);

const SidebarBody = ({ items, onClick, user, logout, label }) => (
  <div className="flex h-full flex-col">
    <Link to="/" className="px-2" data-testid="app-site-link"><Logo light /></Link>
    <p className="mt-6 px-3 text-[10px] font-bold uppercase tracking-[0.25em] text-[#ff8080]">{label}</p>
    <div className="mt-3 flex-1 overflow-y-auto"><NavItems items={items} onClick={onClick} /></div>
    <div className="border-t border-white/10 pt-4">
      <p className="truncate px-3 text-sm font-semibold text-white" data-testid="app-user-name">{user?.name}</p>
      <p className="truncate px-3 text-xs text-white/60">{user?.email}</p>
      <Link to="/" className="mt-3 flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-white"><ExternalLink className="h-3.5 w-3.5" />Voir le site</Link>
      <button onClick={logout} data-testid="logout-btn" className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-white"><LogOut className="h-3.5 w-3.5" />Se déconnecter</button>
    </div>
  </div>
);

export default function AppLayout({ items, label, admin }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-64 bg-[#0F2840] p-4 lg:block">
        <SidebarBody items={items} user={user} logout={logout} label={label} />
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-slate-200 bg-white/85 px-4 backdrop-blur-xl">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="grid h-10 w-10 place-items-center rounded-md lg:hidden" data-testid="app-mobile-menu-btn" aria-label="Menu"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 border-0 bg-[#0F2840] p-4 [&>button]:text-white [&>button]:opacity-90">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SidebarBody items={items} onClick={() => setOpen(false)} user={user} logout={logout} label={label} />
              </SheetContent>
            </Sheet>
            <span className="text-sm font-semibold text-slate-500">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" data-testid="app-header-site-link" className="inline-flex items-center gap-1.5 rounded-full border border-[#0F2840]/20 px-3 py-1.5 text-xs font-bold text-[#0F2840] transition-colors hover:bg-[#0F2840] hover:text-white">
              <ExternalLink className="h-3.5 w-3.5" /><span className="hidden sm:inline">Voir le site</span>
            </Link>
            {admin && (
              <Link to="/admin/parametres" data-testid="app-header-links-btn" className="hidden items-center gap-1.5 rounded-full bg-[#D82C2C] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#b82323] md:inline-flex">
                Liens pour mon site
              </Link>
            )}
            {admin && <NotificationsBell />}
            <span className="hidden rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 sm:inline">Démo · données fictives</span>
          </div>
        </header>
        <main className="mx-auto max-w-7xl p-4 md:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
